import { useDispatch, useSelector } from 'react-redux';
import React, { FunctionComponent } from 'react';
import { getShoppingProductsFromTAC } from '../../util/aactools/kompisConvert';
import { isFixedRoom } from '../../util/room';
import { TOP, RIGHT, LEFT } from '../Popup/alignments';
import Measurements from '../ActionButtons/Measurements';
import { KompisMiniConfigurationSummary } from '@inter-ikea-kompis/react-components';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { translate } from '../../services/L10n';
import IntroPopup from '../Popup/IntroPopup';
import styles from './Footer.module.less';
import platform from '../../util/platform';
import toastMaster from '../../services/toastMaster';
import UndoRedo from '../UndoRedo/UndoRedo';
import WallResizerIcon from '../ActionButtons/WallResizerIcon';
import { selectTacIsEmpty } from '../../state/tac/tacSelectors';
import { selectIsWallResizerActive } from '../../state/scene/sceneSelectors';
import { selectIsRtl } from '../../state/dexfSettings/dexfSettingsSelectors';
import { selectKompisTranslations } from '../../state/translations/translationsSelectors';
import { selectDexfSettings } from '../../state/dexfSettings/dexfSettingsSelectors';
import { selectHasHadUserInteraction } from '../../state/popups/popupsSelectors';
import { actionSetWallResizerInactive } from '../../state/scene';
import {
  selectIsMobile,
  selectIsMobilePortrait,
} from '../../state/userAgent/userAgentSelectors';
import { selectPresentTac } from '../../state/tac/tacReducer/tacSelectors';
import { nextView } from '../../state/navigation';
import { actionEnqueueSheet } from '../../state/sheets/sheetActions';
import { WHATS_INCLUDED } from '../Sheets/WhatsIncludedSheet';
import { selectSheetQueue } from '../../state/sheets/sheetSelectors';
import { thunkHandleErrors } from '../../state/tac/tacThunks';
import { selectCurrentView } from '../../state/navigation/navigationSelectors';
import { selectSavedVpcCode } from '../../state/vpc/vpcSelectors';
import { useKioskIntegration } from '../../hooks/useKioskIntegration';
import { thunkSaveVpc } from '../../state/vpc/vpcThunks';
import { thunkGetShoppingItems } from '../../state/summary/summaryThunks';
import { t } from '../../translations';

export interface Props {
  onNextViewClick: Function;
}

const Footer: FunctionComponent<Props> = ({ onNextViewClick }) => {
  const tac = useSelector(selectPresentTac);
  const wallResizerActive = useSelector(selectIsWallResizerActive);
  const hasHadUserInteraction = useSelector(selectHasHadUserInteraction);
  const kompisTranslations = useSelector(selectKompisTranslations);
  const dexfSettings = useSelector(selectDexfSettings);
  const disableNextButton = useSelector(selectTacIsEmpty);
  const isMobile = useSelector(selectIsMobile);
  const isMobilePortrait = useSelector(selectIsMobilePortrait);
  const isRtl = useSelector(selectIsRtl);
  const sheetQueue = useSelector(selectSheetQueue);
  const currentView = useSelector(selectCurrentView);
  const designCode = useSelector(selectSavedVpcCode);

  const dispatch = useDispatch();

  const { integration, isUpptacka } = useKioskIntegration();

  const [triggerElement, setTriggerElement] =
    React.useState<HTMLElement | null>(null);
  const [nextViewHintVisible, setNextViewHintVisible] = React.useState(false);
  const [hasShownNextViewHint, setHasShownNextViewHint] = React.useState(false);
  const [ecoFeeSheetVisible, setEcoFeeSheetVisible] =
    React.useState<any>(undefined);

  const setWallResizerInactive = () =>
    dispatch(actionSetWallResizerInactive({ nonInteraction: true }));
  const openWhatsIncluded = (event: {
    detail: { triggerElement: React.SetStateAction<HTMLElement | null> };
  }) => {
    dispatch(actionEnqueueSheet({ sheetType: WHATS_INCLUDED }));
    setTriggerElement(event?.detail.triggerElement);
  };
  const renderNextView = () => dispatch(nextView());

  const shoppingProducts = tac ? getShoppingProductsFromTAC(tac) : [];

  /**
   * Handles intro popup closing
   */
  const onPopupClose = () => {
    setHasShownNextViewHint(true);
    setNextViewHintVisible(false);
  };

  /**
   * Handles default next click
   */
  const handleDefaultNextClick = () => {
    renderNextView();
    toastMaster.dismissCurrentToast();
  };

  /**
   * Handles upptäcka summary click
   */
  const handleUpptackaSummaryClick = () => {
    dispatch(thunkGetShoppingItems());
    dispatch(thunkSaveVpc());
  };

  /**
   * Handles click on next button
   */
  const onNext = async () => {
    const errorsArePresent = !!tac?.errors?.length;

    if (errorsArePresent) dispatch(thunkHandleErrors());
    else {
      onNextViewClick();

      isUpptacka && currentView === 'SCENE'
        ? handleUpptackaSummaryClick()
        : handleDefaultNextClick();
    }
  };

  React.useEffect(() => {
    if (designCode && isUpptacka) {
      integration?.summaryClicked(designCode);
    }
  }, [designCode]);

  React.useEffect(() => {
    if (
      !isKiosk &&
      !hasShownNextViewHint &&
      hasHadUserInteraction &&
      tac.items?.length > 0 &&
      tac.errors?.length === 0
    ) {
      setTimeout(() => {
        setHasShownNextViewHint(true);
        setNextViewHintVisible(true);
      }, 5000);
    }
  });

  React.useEffect(() => {
    triggerElement?.focus();
  }, [sheetQueue]);

  /**
   * Handles click on footer
   */
  const onClick = () => {
    wallResizerActive && setWallResizerInactive();
  };

  /**
   * On modal open
   * @param visibleModal
   */
  const onModalOpen = ({ detail: { visibleModal } }: any) => {
    setEcoFeeSheetVisible(visibleModal);
  };

  /**
   * On modal close
   * @param visibleModal
   */
  const onModalClose = ({ detail: { visibleModal } }: any) => {
    setEcoFeeSheetVisible(null);
  };
  const isKiosk = platform.isKiosk;

  return (
    <footer className={styles.Footer} onClick={onClick}>
      <div className={styles.actionButtonsContainer}>
        {!isMobilePortrait && (
          <div className={styles.wallResizerAndMeasurementsWrapper}>
            {isFixedRoom() && <WallResizerIcon />}
            <Measurements />
          </div>
        )}
        {!isMobile && <UndoRedo />}
      </div>

      <div className={styles.footerCheckout}>
        <IntroPopup
          showPopup={nextViewHintVisible}
          alignment={TOP}
          dismissible
          wiggle={true}
          content={translate(t.SPLASH_SCREEN_STEP_FOUR_DESCRIPTION)}
          onClose={onPopupClose}
          arrowAlignment={isRtl ? LEFT : RIGHT}
        >
          <KompisMiniConfigurationSummary
            disabled={disableNextButton}
            shoppingProducts={shoppingProducts}
            onNext={onNext}
            hideWhatsIncludedIcon={isKiosk}
            theme={SkapaTheme}
            translations={kompisTranslations}
            settings={dexfSettings}
            animatedPrice={!isMobile}
            onModalOpen={onModalOpen}
            onModalClose={onModalClose}
            visibleModal={ecoFeeSheetVisible}
            disableLinks={isKiosk}
            onOpenWhatsIncluded={openWhatsIncluded}
            data-testid="next-area"
          />
        </IntroPopup>
      </div>
    </footer>
  );
};

export default Footer;
